import {
  ExclamationTriangleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";

const ImpersonationBanner = () => {
  const [impersonationData, setImpersonationData] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    checkImpersonationStatus();
  }, []);

  // Only poll when we're already impersonating
  useEffect(() => {
    let interval;
    if (impersonationData && isVisible) {
      interval = setInterval(checkImpersonationStatus, 30000); // Every 30 seconds
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [impersonationData, isVisible]);

  const checkImpersonationStatus = async () => {
    try {
      // Use backend URL based on environment
      const isDevelopment = window.location.hostname === "localhost";
      const baseUrl = isDevelopment ? "http://localhost:8000" : "";

      const response = await fetch(`${baseUrl}/api/impersonation/status/`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        console.log("Impersonation status:", data);
        if (data.impersonating) {
          setImpersonationData(data);
          setIsVisible(true);
        } else {
          setImpersonationData(null);
          setIsVisible(false);
        }
      } else {
        console.log("Impersonation status check failed:", response.status);
      }
    } catch (error) {
      console.error("Error checking impersonation status:", error);
    }
  };

  const stopImpersonation = async () => {
    if (
      !window.confirm(
        "Are you sure you want to stop impersonation and return to admin?"
      )
    ) {
      return;
    }

    try {
      // Use backend URL based on environment
      const isDevelopment = window.location.hostname === "localhost";
      const baseUrl = isDevelopment ? "http://localhost:8000" : "";

      const response = await fetch(`${baseUrl}/admin/impersonation/stop/`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        setIsVisible(false);
        setImpersonationData(null);

        // Clear all session data so no stale impersonation state remains
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        localStorage.removeItem("authToken");
        localStorage.removeItem("userInfo");
        localStorage.removeItem("locationInfo");

        // Optionally redirect to admin
        if (
          window.confirm(
            "Impersonation stopped. Would you like to return to admin interface?"
          )
        ) {
          const isDevelopment = window.location.hostname === "localhost";
          const adminUrl = isDevelopment
            ? "http://localhost:8000/admin/"
            : "/admin/";
          window.location.href = adminUrl;
        } else {
          // Refresh the page to update user context
          window.location.reload();
        }
      } else {
        alert("Error stopping impersonation. Please try again.");
      }
    } catch (error) {
      console.error("Error stopping impersonation:", error);
      alert("Error stopping impersonation. Please try again.");
    }
  };

  if (!isVisible || !impersonationData) {
    return null;
  }

  return (
    <div className="bg-red-600 text-white shadow-sm border-b border-red-700 relative z-[300]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between py-3">
          <div className="flex items-center space-x-3">
            <ExclamationTriangleIcon className="h-6 w-6 text-yellow-300" />
            <div className="flex-1">
              <p className="text-sm font-medium">
                <span className="font-bold">ADMIN IMPERSONATION:</span> You are
                viewing the system as{" "}
                <span className="font-semibold">
                  {impersonationData.target_user.full_name ||
                    impersonationData.target_user.username}
                </span>
                {impersonationData.company && (
                  <span>
                    {" "}
                    at{" "}
                    <span className="font-semibold">
                      {impersonationData.company.name}
                    </span>
                  </span>
                )}
              </p>
              <p className="text-xs opacity-90 mt-1">
                Original admin:{" "}
                {impersonationData.original_user?.full_name ||
                  impersonationData.original_user?.username}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={stopImpersonation}
              className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-4 py-1.5 rounded-md text-sm font-medium transition-colors duration-200 flex items-center space-x-1"
            >
              <XMarkIcon className="h-4 w-4" />
              <span>Stop Impersonation</span>
            </button>

            <a
              href="/admin/"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-4 py-1.5 rounded-md text-sm font-medium transition-colors duration-200"
            >
              Admin Panel
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImpersonationBanner;
